/**
 * Fight Club points estimate for the main product form.
 * Markup: snippets/loyalty-points-notice.liquid
 *
 * Reuses the theme's 'on:variant:change' event (detail.form, detail.variant) and the
 * bubbling 'change' event of the form's quantity input. Both are handled on the product
 * section because the quantity input is replaced when the variant changes.
 *
 * Also runs the small print tooltip (native popover where supported, class toggle otherwise).
 */
if (!customElements.get('loyalty-points-notice')) {
  class LoyaltyPointsNotice extends HTMLElement {
    connectedCallback() {
      this.section = this.closest('.js-product');
      if (!this.section) return;

      this.variant = this.dataset.price
        ? { price: Number(this.dataset.price), available: this.dataset.available === 'true' }
        : null;

      this.section.addEventListener('on:variant:change', (evt) => {
        if (evt.detail.form !== this.getForm()) return;
        this.variant = evt.detail.variant;
        this.render();
      });

      this.section.addEventListener('change', (evt) => {
        if (evt.target.name === 'quantity' && evt.target.form === this.getForm()) this.render();
      });

      this.render();
      // Live only after the first render, so the page load value is not announced.
      this.querySelector('.js-points-text').setAttribute('aria-live', 'polite');
      this.initTip();
    }

    /**
     * Small print tooltip: opens on mouse hover, keyboard focus and click/tap; closes on leave,
     * blur, Escape and outside press. Every open needs a fresh event, so a dismissed tooltip
     * stays closed while the trigger is still hovered or focused.
     */
    initTip() {
      const info = this.querySelector('.js-points-info');
      this.trigger = this.querySelector('.js-points-trigger');
      this.tip = this.querySelector('.js-points-tip');
      this.open = false;
      this.hover = false;

      // Without JS the button toggles the popover natively; with JS it is all handled here.
      this.trigger.removeAttribute('popovertarget');
      if (this.tip.showPopover) this.tip.popover = 'manual';

      this.dismiss = (evt) => {
        if (evt.type === 'keydown' ? evt.key === 'Escape' : !info.contains(evt.target)) this.toggleTip(false);
      };

      // The tooltip is inside 'info', so it stays hoverable; the delay covers the gap on the way to it.
      info.addEventListener('pointerenter', (evt) => {
        if (evt.pointerType !== 'mouse') return;
        clearTimeout(this.leaveTimer);
        if (!this.hover) this.toggleTip(true);
        this.hover = true;
      });
      info.addEventListener('pointerleave', (evt) => {
        if (evt.pointerType !== 'mouse') return;
        this.leaveTimer = setTimeout(() => {
          this.hover = false;
          if (!this.trigger.matches(':focus-visible')) this.toggleTip(false);
        }, 150);
      });

      // Keyboard focus only: a tap or click also focuses, and would then toggle it straight back.
      this.trigger.addEventListener('focus', () => {
        if (this.trigger.matches(':focus-visible')) this.toggleTip(true);
      });
      this.trigger.addEventListener('blur', () => {
        if (!this.hover) this.toggleTip(false);
      });
      // A mouse click while hovering keeps it open (or reopens it after Escape).
      this.trigger.addEventListener('click', () => this.toggleTip(this.hover || !this.open));
    }

    toggleTip(open) {
      if (open === this.open) return;
      this.open = open;
      this.trigger.setAttribute('aria-expanded', open);
      this.tip.classList.toggle('is-open', open);
      this.tip[open ? 'showPopover' : 'hidePopover']?.();
      const method = open ? 'addEventListener' : 'removeEventListener';
      document[method]('keydown', this.dismiss);
      document[method]('pointerdown', this.dismiss);
      if (!open || !this.tip.showPopover) return; // No popover: CSS places it above the row.

      // Top layer: centred above the trigger (below if no room), kept inside the viewport.
      // ponytail: placed on open only, page coordinates so it scrolls with the page; reposition on resize if it matters.
      const rect = this.trigger.getBoundingClientRect();
      const maxLeft = document.documentElement.clientWidth - this.tip.offsetWidth - 8;
      const left = Math.max(8, Math.min(rect.left + rect.width / 2 - this.tip.offsetWidth / 2, maxLeft));
      const above = rect.top - this.tip.offsetHeight - 6;
      const top = above < 8 ? rect.bottom + 6 : above;
      this.tip.style.inset = `${top + window.scrollY}px auto auto ${left + window.scrollX}px`;
    }

    getForm() {
      return this.section.querySelector('.js-product-form-main');
    }

    /**
     * Estimates the points earned: 5 per £1, rounded half up to a whole point.
     * @param {?object} variant - Variant with 'price' (pence, never compare-at) and 'available'.
     * @param {number|string} qty - Quantity.
     * @param {string} currency - Active currency ISO code.
     * @returns {?number} Points, or null if no estimate should be shown.
     */
    static estimate(variant, qty, currency) {
      const price = Number(variant?.price);
      const quantity = Number(qty);
      if (currency !== 'GBP' || !variant?.available) return null;
      if (!Number.isSafeInteger(price) || price <= 0 || !Number.isSafeInteger(quantity) || quantity < 1) return null;
      const total = price * quantity * 5 + 50;
      return Number.isSafeInteger(total) ? Math.floor(total / 100) || null : null;
    }

    render() {
      const qty = this.getForm()?.querySelector('[name="quantity"]')?.value;
      const points = LoyaltyPointsNotice.estimate(this.variant, qty, window.Shopify?.currency?.active);
      const pointsEl = this.querySelector('.js-points');
      pointsEl.textContent = points ? points.toLocaleString('en-GB') : '';
      pointsEl.hidden = !points;
      this.querySelector('.js-points-estimate').hidden = !points;
    }
  }

  customElements.define('loyalty-points-notice', LoyaltyPointsNotice);
}
